import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormSection,
  Input,
  RequiredMark,
  Switch,
} from '@/components/ui';
import { RadioRow } from '@/features/shippingFee/components/RadioRow';
import { OptionListEditor } from './OptionListEditor';
import {
  APPLY_SCOPE_LABELS,
  APPLY_SCOPES,
  DEFAULT_FIELD_PAYLOAD,
  FIELD_TYPE_LABELS,
  FIELD_TYPES,
  INPUT_METHOD_LABELS,
  INPUT_METHODS,
  MAX_MAX_LENGTH,
  MIN_MAX_LENGTH,
  ORDER_FORM_MALL_LABELS,
  isOptionFieldType,
  isTextFieldType,
  type OrderFormApplyScope,
  type OrderFormFieldDetail,
  type OrderFormFieldPayload,
  type OrderFormFieldType,
  type OrderFormInputMethod,
  type OrderFormMall,
} from '../types/orderFormField';

interface OrderFormFieldEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 현재 활성 몰 탭(신규 시 기본 mall). */
  mall: OrderFormMall;
  /** 편집 대상(상세). null 이면 신규. */
  target: OrderFormFieldDetail | null;
  saving: boolean;
  onSubmit: (payload: OrderFormFieldPayload) => void;
}

interface EditorDraft {
  label: string;
  description: string;
  displayEnabled: boolean;
  required: boolean;
  fieldType: OrderFormFieldType;
  /** 입력 보존을 위해 문자열로 관리. 제출 시 1~250 클램프. */
  maxLength: string;
  options: string[];
  encrypt: boolean;
  mask: boolean;
  applyScope: OrderFormApplyScope;
  inputMethod: OrderFormInputMethod;
  sortOrder: string;
}

const emptyDraft = (): EditorDraft => ({
  label: '',
  description: '',
  displayEnabled: DEFAULT_FIELD_PAYLOAD.displayEnabled,
  required: DEFAULT_FIELD_PAYLOAD.required,
  fieldType: DEFAULT_FIELD_PAYLOAD.fieldType,
  maxLength: String(DEFAULT_FIELD_PAYLOAD.maxLength),
  options: [],
  encrypt: DEFAULT_FIELD_PAYLOAD.encrypt,
  mask: DEFAULT_FIELD_PAYLOAD.mask,
  applyScope: DEFAULT_FIELD_PAYLOAD.applyScope,
  inputMethod: DEFAULT_FIELD_PAYLOAD.inputMethod,
  sortOrder: '0',
});

const toDraft = (target: OrderFormFieldDetail): EditorDraft => ({
  label: target.label,
  description: target.description,
  displayEnabled: target.displayEnabled,
  required: target.required,
  fieldType: target.fieldType,
  maxLength: String(target.maxLength),
  options: [...target.options],
  encrypt: target.encrypt,
  mask: target.mask,
  applyScope: target.applyScope,
  inputMethod: target.inputMethod,
  sortOrder: String(target.sortOrder),
});

/** 글자수 제한 1~250 클램프(§4 경계). 비정상 입력은 기본값으로 보정. */
const clampMaxLength = (raw: string): number => {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n)) return DEFAULT_FIELD_PAYLOAD.maxLength;
  return Math.min(MAX_MAX_LENGTH, Math.max(MIN_MAX_LENGTH, n));
};

const toInt = (raw: string): number => {
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) ? n : 0;
};

/**
 * 글로벌 주문서 추가정보 항목 생성/편집 다이얼로그. (고도몰 §4 05.png 항목 수정폼.)
 * 노출유형(라디오 5종) → 유형별 동적 입력(텍스트형=글자수 제한 / 선택형=선택지 목록),
 * 노출·필수·암호화·마스킹 토글, 적용 범위·입력방법 라디오. 검증은 be-order 계약 미러링.
 */
export const OrderFormFieldEditor = ({
  open,
  onOpenChange,
  mall,
  target,
  saving,
  onSubmit,
}: OrderFormFieldEditorProps) => {
  const [draft, setDraft] = useState<EditorDraft>(() =>
    target ? toDraft(target) : emptyDraft(),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(target ? toDraft(target) : emptyDraft());
      setError(null);
    }
  }, [open, target]);

  const set = <K extends keyof EditorDraft>(key: K, val: EditorDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: val }));

  const isText = isTextFieldType(draft.fieldType);
  const isOption = isOptionFieldType(draft.fieldType);

  const validate = (): string | null => {
    if (draft.label.trim() === '') return '항목명을 입력하세요.';
    if (isText) {
      const len = clampMaxLength(draft.maxLength);
      if (len < MIN_MAX_LENGTH || len > MAX_MAX_LENGTH) {
        return `글자수 제한은 ${MIN_MAX_LENGTH}~${MAX_MAX_LENGTH} 사이여야 합니다.`;
      }
    }
    if (isOption) {
      const valid = draft.options.map((o) => o.trim()).filter((o) => o !== '');
      if (valid.length === 0) {
        return '라디오·체크·셀렉트 유형은 선택지를 1개 이상 입력하세요.';
      }
    }
    return null;
  };

  const handleSubmit = () => {
    const message = validate();
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    onSubmit({
      mall,
      label: draft.label.trim(),
      description: draft.description.trim(),
      displayEnabled: draft.displayEnabled,
      required: draft.required,
      fieldType: draft.fieldType,
      // 텍스트형만 의미. 그 외 기본값(BE 무시).
      maxLength: isText ? clampMaxLength(draft.maxLength) : DEFAULT_FIELD_PAYLOAD.maxLength,
      // 선택형만 저장. 빈 선택지 제거. 그 외 빈 배열.
      options: isOption
        ? draft.options.map((o) => o.trim()).filter((o) => o !== '')
        : [],
      // 암호화·마스킹은 텍스트형에서만 의미(숫자·영문 입력 제약). 그 외 false.
      encrypt: isText ? draft.encrypt : false,
      mask: isText ? draft.mask : false,
      applyScope: draft.applyScope,
      inputMethod: draft.inputMethod,
      sortOrder: toInt(draft.sortOrder),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            {ORDER_FORM_MALL_LABELS[mall]} 주문서 추가정보{' '}
            {target ? '수정' : '등록'}
          </DialogTitle>
          <DialogDescription>
            노출유형·글자수 제한·필수 여부·암호화/마스킹·적용 범위·입력방법을 설정합니다.
            노출유형에 따라 글자수 제한 또는 선택지 입력이 표시됩니다.
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[66vh] flex-col gap-1 overflow-auto py-1">
          {error ? (
            <div
              className="mb-2 rounded-md px-3 py-2 text-[13px]"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
              role="alert"
            >
              {error}
            </div>
          ) : null}

          {/* 주민등록번호 수집 금지 법령 주의(§4, be-order #3: 항목명·설명에 RRN
              패턴/키워드가 있으면 저장이 400 으로 거부된다). */}
          <div
            className="mb-2 rounded-md px-3 py-2 text-[12px]"
            style={{ background: 'var(--surface-raised)', color: 'var(--content-muted)' }}
          >
            ⚠ 주민등록번호는 법령상 수집이 금지됩니다(개인정보보호법 §24의2). 항목명·설명에
            주민등록번호 형식(13자리)이나 "주민등록·주민번호" 등의 키워드가 포함되면 저장이
            거부됩니다. (居民身份证号·여권번호·위챗 등 외국 식별자는 허용)
          </div>

          <FormSection label="항목명" density="compact" required>
            <Input
              value={draft.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder="예: 居民身份证号"
              maxLength={100}
              disabled={saving}
              data-testid="order-form-field-label"
            />
          </FormSection>

          <FormSection label="설명" density="compact">
            <Input
              value={draft.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="예: 中国大陆居民身份证号/港澳台身份证号"
              maxLength={250}
              disabled={saving}
              data-testid="order-form-field-description"
            />
          </FormSection>

          <FormSection label="노출 상태" density="compact">
            <Switch
              checked={draft.displayEnabled}
              onCheckedChange={(v) => set('displayEnabled', v)}
              disabled={saving}
              aria-label="노출 상태"
              data-testid="order-form-field-display-enabled"
            />
          </FormSection>

          <FormSection label="필수 여부" density="compact">
            <Switch
              checked={draft.required}
              onCheckedChange={(v) => set('required', v)}
              disabled={saving}
              aria-label="필수 여부"
              data-testid="order-form-field-required"
            />
          </FormSection>

          <RadioRow
            name="off-field-type"
            label="노출유형"
            options={FIELD_TYPES}
            labels={FIELD_TYPE_LABELS}
            value={draft.fieldType}
            onChange={(v) => set('fieldType', v)}
            disabled={saving}
          />

          {/* 텍스트형 → 글자수 제한(1~250) + 암호화/마스킹 */}
          {isText ? (
            <>
              <FormSection label="글자수 제한" density="compact">
                <Input
                  type="number"
                  min={MIN_MAX_LENGTH}
                  max={MAX_MAX_LENGTH}
                  inputMode="numeric"
                  value={draft.maxLength}
                  onChange={(e) => set('maxLength', e.target.value)}
                  disabled={saving}
                  className="max-w-[160px] tabular-nums"
                  data-testid="order-form-field-max-length"
                />
              </FormSection>
              <p className="px-1 pb-1 text-[12px]" style={{ color: 'var(--content-muted)' }}>
                1~250자 사이로 입력하세요.
              </p>

              <FormSection label="암호화" density="compact">
                <Switch
                  checked={draft.encrypt}
                  onCheckedChange={(v) => set('encrypt', v)}
                  disabled={saving}
                  aria-label="암호화"
                  data-testid="order-form-field-encrypt"
                />
              </FormSection>
              <FormSection label="마스킹" density="compact">
                <Switch
                  checked={draft.mask}
                  onCheckedChange={(v) => set('mask', v)}
                  disabled={saving}
                  aria-label="마스킹"
                  data-testid="order-form-field-mask"
                />
              </FormSection>
              {draft.encrypt || draft.mask ? (
                <p
                  className="px-1 pb-2 text-[12px]"
                  style={{ color: 'var(--content-muted)' }}
                >
                  ⚠ 암호화·마스킹을 사용하면 해당 항목은 숫자·영문만 입력 가능해집니다.
                </p>
              ) : null}
            </>
          ) : null}

          {/* 선택형 → 선택지 목록 */}
          {isOption ? (
            <div className="py-2">
              <p
                className="mb-2 text-[13px] font-semibold"
                style={{ color: 'var(--content-strong)' }}
              >
                선택지
                <RequiredMark />
              </p>
              <OptionListEditor
                options={draft.options}
                disabled={saving}
                onChange={(options) => set('options', options)}
              />
            </div>
          ) : null}

          <RadioRow
            name="off-apply-scope"
            label="적용 상품"
            options={APPLY_SCOPES}
            labels={APPLY_SCOPE_LABELS}
            value={draft.applyScope}
            onChange={(v) => set('applyScope', v)}
            disabled={saving}
          />

          <RadioRow
            name="off-input-method"
            label="입력방법"
            options={INPUT_METHODS}
            labels={INPUT_METHOD_LABELS}
            value={draft.inputMethod}
            onChange={(v) => set('inputMethod', v)}
            disabled={saving}
          />

          <FormSection label="정렬순서" density="compact">
            <Input
              type="number"
              min={0}
              inputMode="numeric"
              value={draft.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
              disabled={saving}
              className="max-w-[120px] tabular-nums"
              data-testid="order-form-field-sort-order"
            />
          </FormSection>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            취소
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={saving}
            data-testid="order-form-field-save"
          >
            {saving ? '저장 중…' : '저장'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
